// =========================================================
// Amazon Operation OS — Unified Data Model
// SSOT: single source of truth for every module.
// =========================================================

/** SKU 主档（静态属性，人工维护一次即可） */
export interface SkuMaster {
  sku: string;                // 主键（与 siteId 组成复合主键 [sku+siteId]）
  siteId?: string;             // 站点ID（复合主键第二部分，默认 "site_us"）
  name: string;               // 品名
  image?: string;             // 图片 URL / 文件名
  asin?: string;
  linkType?: "main" | "follow" | "backup"; // 主链接 / 跟卖 / 备用
  saleStatus: "active" | "clearance" | "paused" | "discontinued"; // 在售 / 清货 / 停售
  fulfillment: "FBA" | "FBM" | "mixed";  // 仓库类型，mixed=混卖
  fulfillmentMode?: FulfillmentMode;     // 计算模式：FBA_ONLY/FBM_ONLY/FBA_FBM_COMBINED（缺省=严格分拆，不自动合并）
  store: string;               // 所属店铺
  marketplace?: string;        // 站点，默认 US
  price: number;               // 售价
  productUrl?: string;
  competitorUrls?: string[];
  aPlus?: "done" | "todo";     // A+ 是否完成
  category?: string;           // 品类
  lifecycle?: "new" | "growth" | "mature" | "clearance" | "eol"; // 生命周期
  launchDate?: string;         // 上架日期 YYYY-MM-DD
  parentGroup?: string;        // 父记录
  costFob?: number;
  costShipping?: number;       // 头程
  costDelivery?: number;       // 配送费
  costCommission?: number;     // 佣金
  commissionRate?: number;      // 佣金率 %（显式设置时优先于固定 15% 默认）
  costStorage?: number;        // 仓储费
  costReturn?: number;         // 退货费
  costAd?: number;             // 广告费
  // 折扣列成本字段
  discountPrice?: number;       // 折扣售价
  discountFob?: number;
  discountShipping?: number;   // 折扣头程
  discountDelivery?: number;   // 折扣尾程
  discountCommission?: number;
  discountStorage?: number;
  discountReturn?: number;
  discountAd?: number;
  discountCoupon?: number;
  // FBA/FBM 分拆（混卖模式）
  fbaPrice?: number;
  fbmPrice?: number;
  fbaLeadTimeDays?: number;
  fbmLeadTimeDays?: number;
  fbaSafetyStockDays?: number;
  fbmSafetyStockDays?: number;
  leadTimeDays?: number;       // 工厂→FBA 天数（默认 40）
  safetyStockDays?: number;    // 安全库存天数（默认 30）
  moq?: number;                // 最小起订量
  factoryName?: string;        // 默认工厂（基础档案表录入，展示/备货参考）
  /** 组合款公式，如 "BFB052×2+BFB053"。导入时自动累加组件 FOB / 头程费 / 配送费填入本 SKU */
  composeFormula?: string;
  // 运营表扩展字段
  upc?: string;                // UPC
  parentAsin?: string;         // 父体 ASIN
  parentSku?: string;          // 父体 SKU
groupSku?: string;           // 所属父SKU分组，空/未设置表示自己是父SKU/独立SKU
  msku?: string;               // MSKU
  /** 各 MSKU 对应的独立 ASIN（MSKU -> ASIN）。
   *  导入「SKU标识符」/「销量导入」时按行保留，展示/筛选时优先取此值，缺失则回退 asin。
   *  向后兼容：旧数据无此字段时一律用父级 asin。 */
  mskuAsins?: Record<string, string>;
  /** 各 MSKU 对应的独立店铺（MSKU -> storeId/storeName）。
   *  导入「SKU标识符」时按行保留，展示/筛选时优先取此值，缺失则回退 store。
   *  向后兼容：旧数据无此字段时一律用父级 store。 */
  mskuStores?: Record<string, string>;
  /** 各 MSKU 对应的独立链接类型（MSKU -> "main"|"follow"|"backup"）。
   *  导入「SKU标识符」时按行保留，ASIN 显示逻辑据此判断是否回退父级 ASIN。
   *  向后兼容：旧数据无此字段时回退到父级 linkType。 */
  mskuLinkTypes?: Record<string, "main" | "follow" | "backup">;
  /** 各 MSKU 独立的动态指标（按 MSKU 行保留，不取平均）。
   *  导入「销量导入」「运营数据导入」时按行写入，展示层展开 MSKU 时优先取此值。
   *  向后兼容：旧数据无此字段时回退到家族级快照。 */
  mskuMetrics?: Record<string, {
    rating?: number;
    reviewCount?: number;
    adRatio?: number;
    returnRate?: number;
    refundRate?: number;
    sales7d?: number;
    sales30d?: number;
    price?: number;
    shippingFee?: number;
    listPrice?: number;
  }>;
  shippingFee?: number;        // 运费
  listPrice?: number;          // List Price（销售总价 = price + shippingFee）
  coupon?: number;             // 优惠券金额
  packageLength?: number;      // 包裹长 cm
  packageWidth?: number;       // 包裹宽 cm
  packageHeight?: number;      // 包裹高 cm
  unitsPerBox?: number;        // 单箱数
  packageWeight?: number;      // 包裹重 kg
  aPlusAdvanced?: "done" | "todo"; // 高级 A+
  installVideo?: "done" | "todo";  // 安装视频
  transparentPlan?: string;    // 透明计划
}

/** 每日快照（动态指标，系统自动写入 / Excel 每日导入） */
export interface DailySnapshot {
  id?: number;
  date: string;              // YYYY-MM-DD
  sku: string;
  siteId?: string;           // 站点 ID（多站点隔离）
  // 销量
  dailySales7d: number;      // 近 7 天日均
  dailySales30d?: number;    // 近 30 天日均（由 sales30d 导入 ÷30；可选，缺省按 7d）
  monthlySales: number;
  // 库存
  stockOnHand: number;
  stockInTransit: number;
  daysOfCoverOnHand: number;
  daysOfCoverWithTransit: number;
  // 广告 & 利润
  adSpend: number;
  adRatio: number;           // 费比 %
  profit: number;
  profitMargin: number;      // 利润率 %
  profitSource?: "CALCULATED" | "ESTIMATED";  // 利润来源标记（费率联动重算后写入）
  totalCost: number;
  // Review
  rating: number;
  reviewCount?: number;
  returnRate: number;        // 退货率 %
  refundRate?: number;       // 退款率 % (FBM)
  adOrders?: number;         // 广告订单数（选填真实数据；缺省时广告订单占比按广告费比推导）
  // ERP 一键拉取附加字段（可选，非索引，不影响 Dexie schema 版本）
  revenue?: number;          // 销售额（近7天，站点币种）
  acos?: number;             // ACoS %（广告花费/广告销售额）
  refundQty?: number;        // 退款数量（近7天）
  // FBA/FBM 分拆（混卖模式）
  fbaDailySales7d?: number;
  fbmDailySales7d?: number;
  fbaMonthlySales?: number;
  fbmMonthlySales?: number;
  fbaStockOnHand?: number;
  fbmStockOnHand?: number;
  fbaStockInTransit?: number;
  fbmStockInTransit?: number;
  // 折扣利润（有促销时记录）
  discountTotalCost?: number;
  discountProfit?: number;
  discountMargin?: number;
  // 流量 & 转化（业务报告字段，可选——用于「流量端 vs 转化端」归因分流）
  sessions?: number;         // 当期会话数（Sessions）
  orders?: number;           // 当期订单数（Units Ordered）；CVR = orders / sessions
}

/** Listing 归因分流结论：销量/转化下降时，定位问题在流量端还是详情页 */
export type ListingVerdict = "visibility" | "detail_page" | "both" | "none" | "insufficient";

/** 履约计算模式 */
export type FulfillmentMode = "FBA_ONLY" | "FBM_ONLY" | "FBA_FBM_COMBINED";

/** 销售实体（分析层派生，主键 = SKU + 店铺 + 发货方式）
 *  底层 skuMaster / dailySnapshot 仍按家族 SKU 存储，仅在分析层按维度展开。
 *  - FBA_ONLY / FBM_ONLY / 缺省：FBA 与 FBM 严格分开（各自独立卡片）
 *  - FBA_FBM_COMBINED：运营明确选择后合并（库存/销量/在途汇总，底层仍独立） */
export interface SalesEntity {
  key: string;                 // `${sku}__${store}__${fulfillment}`
  sku: string;
  skuName: string;
  store: string;
  marketplace: string;
  fulfillment: "FBA" | "FBM" | "COMBINED";
  mode: FulfillmentMode;
  dailySales: number;          // 当前口径日均（按 config.salesBasis）
  dailySales7d: number;
  dailySales30d: number;
  stockOnHand: number;
  stockInTransit: number;
  fbaStockOnHand: number;
  fbmStockOnHand: number;
  fbaStockInTransit: number;
  fbmStockInTransit: number;
}

/** 分仓库存切片 */
export interface InventoryLayer {
  id?: number;
  date: string;
  sku: string;
  siteId?: string;           // 站点 ID（多站点隔离）
  fbaStock: number;
  fbmStock: number;
  factoryStock: number;
  eastTransit: number;
  westTransit: number;
  southeast: number;    // 乐歌美东南（兼容旧数据）
  southcentral: number; // 乐歌美中南（兼容旧数据）
  // 新：4 区域 × 在库 / 在途
  eastStock?: number;
  westStock?: number;
  southeastStock?: number;
  southcentralStock?: number;
  southeastTransit?: number;
  southcentralTransit?: number;
  warehouseBreakdown?: WarehouseStock[];  // 海外仓拆分明细
  transitBatches?: TransitBatch[];       // 在途批次明细
  factoryBatches?: FactoryBatch[];         // 工厂批次明细
}

/** 活动配置 */
export interface Campaign {
  id: string;
  siteId?: string;           // 站点隔离
  name: string;              // Prime Day / BF / Xmas
  startDate: string;
  endDate: string;
  multiplier: number;        // 销量倍率
  skus?: string[];           // 参与 SKU，空=全站
  active: boolean;
  discountPrice?: number;    // 大促期间预估折扣售价，用于利润模拟
}

/** 促销 / Deal 活动
 *  支持标准类型 + 自定义命名（type=custom 时用 customTypeName 显示） */
export type PromotionType =
  | "BD"              // Best Deal
  | "LD"              // Lightning Deal
  | "Coupon"
  | "Price Discount"
  | "Promotion"
  | "custom";         // 自定义（用 customTypeName 展示）

export interface Promotion {
  id: string;
  siteId?: string;           // 站点隔离
  sku: string;
  skuName?: string;
  /** 子链接 MSKU：促销针对特定 MSKU（如跟卖链接）而非父 SKU 时填写 */
  msku?: string;
  store: string;
  type: PromotionType;
  /** 自定义类型名称：type=custom 时必填，其他类型忽略 */
  customTypeName?: string;
  name: string;
  startDate: string;
  endDate: string;
  status: "upcoming" | "active" | "ended";
  notes?: string;
  multiplier?: number;
  discountPrice?: number;    // 折扣价格（运营表中的折扣售价）
  // —— 内嵌促销成本（活动创建即录，实现"一次录入，全局联动"）——
  costMode?: "amount" | "rate";   // 缺省=无独立促销成本；amount=金额模式，rate=折扣率模式
  amount?: number;                // 金额模式：该活动促销成本 USD
  rate?: number;                  // 折扣率模式：如 20 表示 20%
  estimatedCost?: number;         // rate 模式自动估算（rate% × 售价 × 周销量）
  promoCost?: number;             // 【冗余缓存】计算后有效成本，供时间线/汇总快速取用
}

/** 手动促销成本类型（优惠券/秒杀/站外折扣） */
export type ManualPromoType = "coupon" | "flash_sale" | "offsite_discount" | "other";

/** 手动促销成本记录 — 存 IndexedDB manualPromotions 表 */
export interface ManualPromotion {
  id: string;
  siteId?: string;           // 站点隔离
  sku: string;
  skuName?: string;
  type: ManualPromoType;
  startDate: string;          // 起 YYYY-MM-DD
  endDate: string;            // 止 YYYY-MM-DD
  costMode: "amount" | "rate";
  amount?: number;            // 金额模式：直接填促销成本金额 (USD)
  rate?: number;              // 折扣率模式：如 20 表示 20%
  estimatedCost?: number;    // 折扣率模式自动估算：rate% × 售价 × 周销量
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

/** 风险 / 告警 */
export type AlertType =
  | "stockout"
  | "low_stock"
  | "overstock"
  | "profit"
  | "ad"
  | "rating"
  | "review"
  | "return"
  | "listing"
  | "traffic_drop"     // 流量下滑（Sessions 周环比）
  | "promo_start"      // 促销即将开始
  | "promo_end";        // 促销即将到期

export type AlertSeverity = "critical" | "warning" | "info";

/** 告警类型中文名（异常中心/归因诊断共用） */
export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  stockout: "断货风险",
  low_stock: "库存紧张",
  overstock: "库存积压",
  profit: "利润异常",
  ad: "广告异常",
  rating: "评分下降",
  return: "退款异常",
  review: "差评激增",
  listing: "Listing 待优化",
  traffic_drop: "流量下滑",
  promo_start: "促销即将开始",
  promo_end: "促销即将到期",
};

/** 告警类型 → 归因诊断类型映射（无映射的类型不显示诊断入口） */
export const ALERT_TYPE_TO_DIAG: Partial<Record<AlertType, "profit" | "sales" | "rating" | "adRatio">> = {
  profit: "profit",
  ad: "adRatio",
  rating: "rating",
  review: "rating",
  return: "rating",
  stockout: "sales",
  low_stock: "sales",
  overstock: "sales",
  traffic_drop: "sales",
};

/**
 * 告警可下钻的归因诊断类型：类型映射优先；
 * 无映射但带分流结论（流量端/转化端/双端）的告警（如 Listing 待优化）兜底到
 * 销量下降诊断——其「归因结论」区正是分流判定与 Sessions/CVR 对比卡。
 * none / insufficient 不给入口（没有可看的异常）。
 */
export function resolveAlertDiagType(
  a: Pick<Alert, "type" | "listingVerdict">,
): "profit" | "sales" | "rating" | "adRatio" | undefined {
  if (ALERT_TYPE_TO_DIAG[a.type]) return ALERT_TYPE_TO_DIAG[a.type];
  return a.listingVerdict === "visibility" || a.listingVerdict === "detail_page" || a.listingVerdict === "both"
    ? "sales"
    : undefined;
}

export interface Alert {
  id: string;
  siteId?: string;           // 站点隔离
  date: string;
  sku: string;
  skuName?: string;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  detail: string;
  metric?: number;
  suggestion?: string;
  status: "open" | "handled" | "ignored";
  // Listing 归因分流（展示层动态计算注入，见 domain/listingVerdict.ts；存储层可不落盘）
  listingVerdict?: ListingVerdict;
  sessionsCurrent?: number;
  sessionsPrevious?: number;
  cvrCurrent?: number;
  cvrPrevious?: number;
}

/** 发货建议 */
export interface ShipmentSuggestion {
  sku: string;
  skuName: string;
  image?: string;
  currentStock: number;
  inTransit: number;
  dailySales: number;
  daysOfCoverOnHand: number;
  daysOfCoverWithTransit: number;
  leadTimeDays: number;
  safetyStockDays: number;
  targetCoverDays: number;
  suggestQty: number;
  latestShipDate: string;    // 建议最晚发货日
  priority: "urgent" | "high" | "normal" | "low";
  reason: string;
  campaignBoost?: string;
}

/** 利润计算中「退款损失」取数口径：退款率（默认，主口径）/ 退货率（备选） */
export type RefundBasis = "refund" | "return";

/** 全局参数 */
export interface GlobalConfig {
  defaultLeadTime: number;         // 默认 Lead Time
  defaultSafetyStockDays: number;  // 默认安全库存天数
  defaultTargetCoverDays: number;  // 默认目标库存天数
  salesBasis?: "7d" | "30d";       // 日均销量口径（缺省 7d，可在参数中心切 30d）
  profitMarginThreshold: number;   // 利润率异常阈值 %
  adRatioThreshold: number;        // 费比异常阈值 %
  targetAdRatio?: number;          // 目标广告费比 %（异常中心「估算日超支」基准，缺省 25）
  ratingDropThreshold: number;     // 评分下降阈值
  returnRateThreshold: number;     // 退款率异常阈值 %
  returnBasis?: RefundBasis;       // 利润计算退款损失口径（缺省 refund）
  lifecycleNewDays: number;        // 新品期天数
  lifecycleGrowthDays: number;     // 成长期天数
}

/** 发货建议公式配置（存 config key "shipmentFormula"） */
export type ShipmentRounding = "ceil" | "floor" | "round";
export type ShipmentThresholdMode =
  | "lt_safety"     // Lead Time + 安全库存
  | "lt_only"       // 仅 Lead Time
  | "safety_only"   // 仅安全库存
  | "custom_days";  // 自定义天数

export interface ShipmentFormulaConfig {
  salesBasis: "7d" | "30d";             // 默认日均销量口径（发货页可临时切换）
  rounding: ShipmentRounding;           // 建议量取整方式
  thresholdMode: ShipmentThresholdMode; // 触发阈值方式
  customThresholdDays: number;          // thresholdMode=custom_days 时的阈值天数
  promoMultiplierEnabled: boolean;      // 活动倍率开关
  promoExtraDays: number;               // 活动期间目标天数额外加的天数
}

export const DEFAULT_SHIPMENT_FORMULA: ShipmentFormulaConfig = {
  salesBasis: "7d",
  rounding: "ceil",
  thresholdMode: "lt_safety",
  customThresholdDays: 60,
  promoMultiplierEnabled: true,
  promoExtraDays: 30,
};

/** 利润公式可自定义的条目（值为表达式文本，留空/非法 = 用内置默认公式） */
export type ProfitFormulaKey =
  | "totalCost"          // 总成本
  | "discountTotalCost"  // 折扣总成本
  | "grossProfit"        // 单件净利
  | "grossMargin"        // 净利率（百分数口径）
  | "adRatio"            // 广告费比（百分数口径）
  | "returnRate"         // 退货率（百分数口径）
  | "discountAdEst"      // 折扣广告费(估)
  | "discountReturnEst"; // 折扣退款费(估)

/** 利润计算公式配置（存 config key "profitFormula"） */
export interface ProfitFormulaConfig {
  costItems: {
    fob: boolean;         // FOB 成本
    shipping: boolean;    // 头程运费
    delivery: boolean;    // 配送费
    commission: boolean;  // 佣金
    storage: boolean;     // 仓储费
    ad: boolean;          // 广告费
    refundLoss: boolean;  // 退货损失（退款率×售价）
    promoCost: boolean;   // 促销成本
    coupon: boolean;      // 优惠券
  };
  discountAdUplift: number;     // 折扣广告费预估上浮 %（默认 10 = ×1.1）
  discountReturnUplift: number; // 折扣退款费预估上浮 %（默认 10 = ×1.1）
  defaultCommissionRate: number; // 默认佣金率 %（无显式佣金/佣金率时按此估算，默认 15）
  formulas: Partial<Record<ProfitFormulaKey, string>>; // 自定义表达式（空 = 默认公式）
}

export const DEFAULT_PROFIT_FORMULA: ProfitFormulaConfig = {
  costItems: {
    fob: true, shipping: true, delivery: true, commission: true,
    storage: true, ad: true, refundLoss: true, promoCost: true, coupon: true,
  },
  discountAdUplift: 10,
  discountReturnUplift: 10,
  defaultCommissionRate: 15,
  formulas: {},
};

/** 各自定义公式的说明：展示名、默认表达式、可用变量 */
export const PROFIT_FORMULA_META: Record<ProfitFormulaKey, { label: string; defaultExpr: string; vars: string; hint?: string }> = {
  totalCost: {
    label: "总成本",
    defaultExpr: "FOB + 头程运费 + 配送费 + 佣金 + 仓储费 + 广告费 + 退款损失 + 促销成本 + 优惠券",
    vars: "售价、FOB、头程运费、配送费、佣金、仓储费、广告费、退货费、退款损失、促销成本、优惠券（各项已按上方开关处理，关闭项为 0）",
  },
  discountTotalCost: {
    label: "折扣总成本",
    defaultExpr: "与总成本相同（各项取折扣口径的值）",
    vars: "同总成本（变量值为折扣口径；「售价」为折扣售价）",
  },
  grossProfit: {
    label: "单件净利",
    defaultExpr: "售价 − 总成本",
    vars: "售价、总成本",
  },
  grossMargin: {
    label: "净利率",
    defaultExpr: "净利 ÷ 售价 × 100",
    vars: "净利、售价、总成本",
    hint: "结果为百分数（15 表示 15%）",
  },
  adRatio: {
    label: "广告费比",
    defaultExpr: "广告费 ÷ 售价 × 100",
    vars: "广告费、售价",
    hint: "结果为百分数",
  },
  returnRate: {
    label: "退货率",
    defaultExpr: "退货费 ÷ 售价 × 100",
    vars: "退货费、售价",
    hint: "仅诊断展示，不计入成本；结果为百分数",
  },
  discountAdEst: {
    label: "折扣广告费(估)",
    defaultExpr: "正常广告费 × (1 + 上浮率)",
    vars: "正常广告费、上浮率（= 设置的上浮率 ÷ 100，如 10 表示 0.1）",
  },
  discountReturnEst: {
    label: "折扣退款费(估)",
    defaultExpr: "正常退款费 × (1 + 上浮率)",
    vars: "正常退款费、上浮率（= 设置的上浮率 ÷ 100）",
  },
};

/** 云端同步配置（GitHub） */
export interface CloudConfig {
  provider: "github";
  token: string;
  owner: string;
  repo: string;
  branch: string;
  path: string;              // 存放文件路径
  lastSyncAt?: string;
}

/** 海外仓库存拆分 */
export interface WarehouseStock {
  warehouse: string;        // 美西 / 美东 / 美中南 / 美东南
  qty: number;
  daysOfCover: number;      // 该仓可售天数 = qty / daily
}

/** 在途批次明细 */
export interface TransitBatch {
  warehouse: string;           // 物流商-目的仓，如"无忧达-美东南"
  qty: number;
  etaDate: string;               // 预计到仓（保留原始文本，支持多行/中文/件数前缀等格式）
  shipDate?: string;             // 出港日期
  pickupDate?: string;           // 提柜日期
  arrivalDate?: string;          // 到港/卸船日期
  statusText?: string;           // 自定义状态文字，如"已卸船"
  shipMethod: "sea" | "air" | "express";
  status: "in_transit" | "at_port" | "customs" | "receiving";
}

/** 工厂批次明细 */
export interface FactoryBatch {
  factoryName: string;
  qty: number;                   // 当前工厂在产/库存数量
  totalQty?: number;             // 该批次下单总量
  deliveryDate: string;          // 交期（保留原始文本）
  status: "producing" | "ready" | "shipped";
}

// ═══════ 海外仓费率配置 ═══════
export interface RateTier {
  min: number;
  max?: number;       // undefined = 无上限
  rate: number;       // USD 费率
}

/**
 * 仓库区域：预置美东/美西/东南/中南四仓，支持用户自定义区域
 * （自定义区域 code = 用户输入名称，如 "加拿大东仓"）。
 */
export type WarehouseRegion = string;

export interface WarehouseRegionOption {
  code: string;   // 存储键；预置四仓用英文 code，自定义区域与 label 相同
  label: string;  // 展示名
}

export const DEFAULT_WAREHOUSE_REGIONS: WarehouseRegionOption[] = [
  { code: "east", label: "美东" },
  { code: "west", label: "美西" },
  { code: "southeast", label: "东南" },
  { code: "southcentral", label: "中南" },
];

export interface WarehouseMapping {
  id?: number;
  warehouseName: string;   // 原始仓库名，如 "乐歌(新) CAP"
  region: WarehouseRegion; // 映射到的区域
  siteId?: string;         // 适用站点；空 = 通用映射（所有站点生效）
  alias?: string;          // 自定义显示名（可选，空 = 用原始仓库名）
  createdAt: string;
}

export interface WarehouseProvider {
  id: string;
  name: string;                 // 服务商名称，如"无忧达"
  billingMode: "days_tier" | "longest_edge_tier"; // 天数阶梯 / 最长边阶梯
  unit: "cbm_per_day" | "cft_per_day";            // 费率/立方米/天 或 费率/立方尺/天
  currency?: string;            // 费率计价币种（ISO 代码，如 CAD/EUR）；缺省 = 按站点币种直接录入
  tiers: RateTier[];            // 服务商级费率表（不分仓库）
}

// ═══════ 新品测算 ═══════
export interface EstimateInput {
  id?: string;
  name?: string;               // 品名 选填
  length: number;              // 长 cm
  width: number;               // 宽 cm
  height: number;              // 高 cm
  weight: number;              // 重量 kg
  fobCny: number;              // FOB CNY
  shippingCost: number;        // 头程 USD
  expectedPrice: number;       // 预计售价 USD
  commissionRate: number;      // 佣金比例 % 默认15
  adBudget: number;            // 广告费预算 USD/月
  fbaDelivery: number;         // FBA配送费 USD/件
  fbaStorage: number;          // FBA仓储费 USD/件
  overseasProviderId?: string; // 选中的海外仓服务商
  overseasWarehouseId?: string;// 选中的海外仓仓库
  overseasDays: number;        // 在库天数 默认30
  returnRate: number;          // 退货率 % 默认值
  coupon: number;              // 优惠券 USD
  createdAt?: string;
}

export interface EstimateResult {
  volumeCbm: number;           // 体积 立方米
  volumeCft: number;           // 体积 立方尺
  fobUsd: number;              // FOB USD (CNY/7.2)
  totalCost: number;
  grossProfit: number;
  grossMargin: number;
  adRatio: number;
  fbaStorageCost: number;      // FBA仓储费（手动）
  overseasStorageCost: number; // 海外仓仓储费（自动算）
}

// ═══════ 新品测算记录 ═══════
export interface CalculationRecord {
  id: string;
  siteId?: string;           // 站点隔离
  sku?: string;                // 关联 SKU（如果保存到已有 SKU）
  name: string;               // 品名
  asin?: string;
  marketplace: string;        // 站点
  exchangeRate?: number;      // 汇率 (CNY→本地货币)
  commissionRate?: number;    // 佣金率 (%)
  adRate?: number;            // 广告率 (%)
  returnRate?: number;        // 退货率 (%)
  storageDays?: number;       // 仓储天数
  price: number;              // 售价
  costFob: number;            // FOB USD
  costShipping: number;       // 头程 USD
  costDelivery: number;       // 配送费 USD
  costCommission: number;     // 佣金 USD
  costStorage: number;        // 仓储费 USD
  costAd: number;             // 广告费 USD
  costReturn: number;         // 退货费 USD
  coupon: number;             // 优惠券 USD
  totalCost: number;
  grossProfit: number;
  grossMargin: number;
  roi: number;
  deliveryMode: "FBA" | "FBM";
  fbaDelivery?: number;
  fbaStorage?: number;
  schemeName?: string;        // FBM 模式下的方案名称（承运商/海外仓名称）
  isBestScheme?: boolean;     // 是否为最优方案
  createdAt: string;
  notes?: string;
}

// ═══════ 待办事项 ═══════
export interface TodoItem {
  id: string;
  siteId?: string;           // 站点隔离
  content: string;             // 必填，文字
  relatedSku?: string;         // 选填，关联SKU
  relatedMsku?: string;        // 选填，关联MSKU（子链接）
  dueDate?: string;             // 选填，截止日 YYYY-MM-DD
  completed: boolean;           // 完成状态
  createdAt: string;            // 创建时间
  completedAt?: string;         // 完成时间
}// ═══════ 店铺管理 ═══════
export interface Shop {
  id: string;
  siteId?: string;           // 站点隔离
  name: string;
  createdAt: string;
}
// ═══════ 健康评分（SKU 综合健康） ═══════
export interface HealthFactor {
  key: string;               // 因子标识（唯一）
  label: string;             // 展示名
  impact: number;            // 扣分影响
}
export interface HealthScore {
  score: number;             // 综合分（0-100）
  level: "健康" | "关注" | "风险";
  factors: HealthFactor[];   // 命中风险因子（扣分项）
}
// ═══════ 运营操作记录（SKU操作日志） ═══════
/** 异常类型（用于"诊断→处理"闭环关联） */
export type AnomalyType =
  | "stockout"      // 断货
  | "low_stock"     // 库存紧张
  | "overstock"     // 库存积压
  | "profit"        // 利润异常
  | "ad"            // 广告异常
  | "rating"        // 评分下降
  | "return"        // 退货异常
  | "listing"       // Listing 待优化
  | "other";        // 其他

export interface OpsLog {
  id?: string;
  siteId?: string;         // 站点隔离
  sku: string;             // 所属 SKU（父SKU或MSKU）
  msku?: string;           // 具体 MSKU（选填，关联到具体子SKU）
  skuName?: string;        // 品名（冗余，方便展示）
  date: string;            // 操作日期 YYYY-MM-DD
  action: string;          // 处理动作，如"降价"、"开广告"、"优化Listing"
  detail?: string;         // 详细说明（旧字段，向后兼容）
  impact?: string;         // 销量影响描述，如"销量上涨约30%"
  // ── 增强字段（贴合"记录处理过程"：日期/异常类型/原因/处理动作/备注）──
  anomalyType?: AnomalyType; // 关联的异常类型（来自诊断）
  reason?: string;         // 原因
  note?: string;           // 备注
  promotionId?: string;    // 关联的促销活动 ID（与促销活动页 SKU↔促销 一致）
  promotionName?: string;  // 关联促销活动名称（冗余，方便展示）
  createdAt: string;       // 记录创建时间
}

// ═══════ 异常诊断备注（留痕：这段时间做了什么导致指标变化） ═══════
export type DiagnosisType = "profit" | "sales" | "rating" | "adRatio";

export interface DiagnosisNote {
  id: string;
  siteId?: string;
  sku: string;
  skuName?: string;
  anomalyType: DiagnosisType;
  content: string;
  /** 记录时使用的对比口径，如「2026-10-15 ~ 2026-10-21 vs 2026-10-08 ~ 2026-10-14」 */
  periodLabel?: string;
  createdAt: string;
  updatedAt?: string;
}

// ==================== 多币种汇率（任意货币对，用户自定义） ====================
export interface ExchangeRate {
  id: string;              // `${from}_${to}`，如 USD_CNY
  from: string;            // 源货币代码（自定义，如 USD / GBP / CNY）
  to: string;              // 目标货币代码
  rate: number;            // 1 from = rate to
  updatedAt: string;
}

// ==================== 多站点架构 ====================
export interface Site {
  id: string;
  name: string;
  marketplace: string;
  currency: string;
  currencySymbol: string;
  exchangeRateToUsd: number;
  cnyToUsdRate?: number;
  commissionRate: number;
  fbaDeliveryFee?: number;
  vatRate?: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt?: string;
}

export interface SiteConfig {
  siteId: string;
  defaultLeadTime: number;
  defaultSafetyStockDays: number;
  defaultTargetCoverDays: number;
  profitMarginThreshold: number;
  adRatioThreshold: number;
  ratingDropThreshold: number;
  returnRateThreshold: number;
  lifecycleNewDays: number;
  lifecycleGrowthDays: number;
}

export interface CrossSiteSummary {
  siteId: string;
  siteName: string;
  currency: string;
  exchangeRateToUsd: number;
  totalSalesUsd: number;
  totalProfitUsd: number;
  totalSkuCount: number;
  totalStockOnHand: number;
  alertCount: number;
}

export interface CrossSiteReport {
  sites: CrossSiteSummary[];
  grandTotalSalesUsd: number;
  grandTotalProfitUsd: number;
  grandTotalSkuCount: number;
  grandTotalAlertCount: number;
}
// ==================== 多站点架构 ====================
export interface Site {
  id: string;
  name: string;
  marketplace: string;
  currency: string;
  currencySymbol: string;
  exchangeRateToUsd: number;
  cnyToUsdRate?: number;
  commissionRate: number;
  fbaDeliveryFee?: number;
  vatRate?: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt?: string;
}

export interface SiteConfig {
  siteId: string;
  defaultLeadTime: number;
  defaultSafetyStockDays: number;
  defaultTargetCoverDays: number;
  profitMarginThreshold: number;
  adRatioThreshold: number;
  ratingDropThreshold: number;
  returnRateThreshold: number;
  lifecycleNewDays: number;
  lifecycleGrowthDays: number;
}

export interface CrossSiteSummary {
  siteId: string;
  siteName: string;
  currency: string;
  exchangeRateToUsd: number;
  totalSalesUsd: number;
  totalProfitUsd: number;
  totalSkuCount: number;
  totalStockOnHand: number;
  alertCount: number;
}

export interface CrossSiteReport {
  sites: CrossSiteSummary[];
  grandTotalSalesUsd: number;
  grandTotalProfitUsd: number;
  grandTotalSkuCount: number;
  grandTotalAlertCount: number;
}
// ==================== 多站点架构 ====================
export interface Site {
  id: string;
  name: string;
  marketplace: string;
  currency: string;
  currencySymbol: string;
  exchangeRateToUsd: number;
  cnyToUsdRate?: number;
  commissionRate: number;
  fbaDeliveryFee?: number;
  vatRate?: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt?: string;
}

export interface SiteConfig {
  siteId: string;
  defaultLeadTime: number;
  defaultSafetyStockDays: number;
  defaultTargetCoverDays: number;
  profitMarginThreshold: number;
  adRatioThreshold: number;
  ratingDropThreshold: number;
  returnRateThreshold: number;
  lifecycleNewDays: number;
  lifecycleGrowthDays: number;
}

export interface CrossSiteSummary {
  siteId: string;
  siteName: string;
  currency: string;
  exchangeRateToUsd: number;
  totalSalesUsd: number;
  totalProfitUsd: number;
  totalSkuCount: number;
  totalStockOnHand: number;
  alertCount: number;
}

export interface CrossSiteReport {
  sites: CrossSiteSummary[];
  grandTotalSalesUsd: number;
  grandTotalProfitUsd: number;
  grandTotalSkuCount: number;
  grandTotalAlertCount: number;
}
// ==================== 多站点架构 ====================
export interface Site {
  id: string;
  name: string;
  marketplace: string;
  currency: string;
  currencySymbol: string;
  exchangeRateToUsd: number;
  cnyToUsdRate?: number;
  commissionRate: number;
  fbaDeliveryFee?: number;
  vatRate?: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt?: string;
}

export interface SiteConfig {
  siteId: string;
  defaultLeadTime: number;
  defaultSafetyStockDays: number;
  defaultTargetCoverDays: number;
  profitMarginThreshold: number;
  adRatioThreshold: number;
  ratingDropThreshold: number;
  returnRateThreshold: number;
  lifecycleNewDays: number;
  lifecycleGrowthDays: number;
}

export interface CrossSiteSummary {
  siteId: string;
  siteName: string;
  currency: string;
  exchangeRateToUsd: number;
  totalSalesUsd: number;
  totalProfitUsd: number;
  totalSkuCount: number;
  totalStockOnHand: number;
  alertCount: number;
}

export interface CrossSiteReport {
  sites: CrossSiteSummary[];
  grandTotalSalesUsd: number;
  grandTotalProfitUsd: number;
  grandTotalSkuCount: number;
  grandTotalAlertCount: number;
}